import type { BaseConfigDefinition } from "@mb/common";
import { BaseConfig, maskSensitiveValue, parseValue } from "@mb/common";
import { clientLogger } from "../logger/client.logger";

// Client Configuration System
export interface ClientConfigDefinition extends BaseConfigDefinition {
    key: string;
    required: boolean;
    defaultValue?: string | boolean | number;
}

export class ClientConfig extends BaseConfig<ClientConfigDefinition> {
    private static instance: ClientConfig | null = null;

    private constructor() {
        super();
    }

    // Get singleton instance
    static getInstance(): ClientConfig {
        if (!ClientConfig.instance) {
            ClientConfig.instance = new ClientConfig();
        }
        return ClientConfig.instance;
    }

    // Reset instance (useful for testing)
    static resetInstance(): void {
        ClientConfig.instance = null;
    }

    // Override initialize to add summary logging
    override initialize(): void {
        super.initialize();
        this.logConfigurationSummary();
    }

    // Log a summary of all client configuration
    private logConfigurationSummary(): void {
        if (typeof process !== 'undefined' && process.env["NODE_ENV"] === 'production') {
            return;
        }

        clientLogger.info('📋 Client Configuration Summary');
        clientLogger.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        const allValues = this.getAll();
        const sortedKeys = Object.keys(allValues).sort();

        for (const key of sortedKeys) {
            const value = allValues[key];
            const definition = this.definitions.get(key);
            const maskedValue = maskSensitiveValue(key, value);
            const requiredLabel = definition?.required ? '[REQUIRED]' : '[OPTIONAL]';
            const isDefault = definition?.defaultValue !== undefined && value === definition.defaultValue;
            const defaultLabel = isDefault ? '(default)' : '';

            clientLogger.info(`  ${requiredLabel} ${key}: ${maskedValue} ${defaultLabel}`);
        }

        clientLogger.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }

    // Implement abstract methods
    protected getValueFromSource(key: string): any {
        return getClientValue(key);
    }

    protected getConfigType(): string {
        return 'Client';
    }
}

// Create and export the main client configuration instance (singleton)
export const clientConfig = ClientConfig.getInstance();

// Export helper functions
export const createClientConfigItem = (key: string, required = false, defaultValue?: string | boolean | number): ClientConfigDefinition => ({
    key,
    required,
    defaultValue
});

export const extendClientConfig = (items: ClientConfigDefinition[]): void => {
    clientConfig.defineMany(items);
};

// Client-side configuration utility functions
export function setClientConfig(config: Record<string, any>): void {
    if (typeof window !== 'undefined') {
        (window as any).__ENV__ = { ...(window as any).__ENV__, ...config };
        if (typeof process !== 'undefined' && process.env["NODE_ENV"] !== 'production') {
            clientLogger.info('[CONFIG] Client configuration set via window.__ENV__');
        }
    }
}


export function setClientConfigGlobal(config: Record<string, any>): void {
    if (typeof window !== 'undefined') {
        (window as any).__CONFIG__ = { ...(window as any).__CONFIG__, ...config };
        clientLogger.info('[CONFIG] Client configuration set via window.__CONFIG__');
    }
}

export function getClientConfig(): Record<string, any> {
    if (typeof window !== 'undefined') {
        return {
            ...(window as any).__ENV__,
            ...(window as any).__CONFIG__
        };
    }
    return {};
}

// export function clearClientConfig(): void {
//     if (typeof window !== 'undefined') {
//         delete (window as any).__ENV__;
//         delete (window as any).__CONFIG__;
//         clientLogger.info('[CONFIG] Client configuration cleared');
//     }
// }


export function getClientValue(key: string): any {
    // Check window.__ENV__ (set by your app)
    if (typeof window !== 'undefined' && (window as any).__ENV__) {
        const value = (window as any).__ENV__[key];
        if (value !== undefined) {
            return parseValue(value);
        }
    }

    // Check window.__CONFIG__ (alternative pattern)
    if (typeof window !== 'undefined' && (window as any).__CONFIG__) {
        const value = (window as any).__CONFIG__[key];
        if (value !== undefined) {
            return parseValue(value);
        }
    }

    // Check meta tags (common pattern for SSR apps)
    if (typeof document !== 'undefined') {
        const metaTag = document.querySelector(`meta[name="config-${key}"]`);
        if (metaTag && metaTag.getAttribute('content')) {
            return parseValue(metaTag.getAttribute('content')!);
        }
    }

    // Vite inlines only static `import.meta.env.FOO` (not dynamic env[key]).
    // Apps should pass DSN via `window.__ENV__` (SSR) or call `initializeSentryConfig({ dsn })`.
    return undefined;
}